// Data Perusahaan, section Dokumen perusahaan: the upload form and the list.
//   upload   card   file (Word, PDF, Excel, teks; 10 MB), Jenis dokumen,
//                   Kategori (Persyaratan or Umum), Judul (optional) and,
//                   in a group, Bagikan ke grup (off keeps it private)
//   list     rows   title, jenis, kategori, Grup or Pribadi, uploader, date
//                   and size, the index state (Diproses, Siap dipakai
//                   Pluto, Gagal diproses with Proses ulang), Unduh, and for
//                   who may manage it Ubah and Hapus (confirmed)
import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import * as API from "../api";
import type { CompanyDocKind, CompanyDocType, CompanyDocument, CompanyGroup } from "../api";
import { formatCount, formatDate } from "../lib/format";
import { saveFile } from "../pbj/docx";
import { formatFileSize } from "../project/files";
import { Menu } from "../ui/Menu";
import { Badge, Dialog, EmptyState, Field, Notice, SkeletonRows, Switch, fieldAria } from "../ui/components";
import { FilePickField } from "../ui/FilePickField";
import { Glyph } from "../ui/icons";
import { companyErrorMessage, DOC_TYPE_LABEL, DOC_TYPES, INDEX_LABEL, INDEX_TONE, KIND_LABEL, needsReload, UPLOAD_ACCEPT, uploadProblem } from "./labels";

/** How often the list is read again while a document is still Diproses. */
export const PENDING_POLL_MS = 5000;

export interface CompanyDocumentsProps {
  group: CompanyGroup | null;
  documents: CompanyDocument[] | null;
  loading: boolean;
  error: unknown;
  reload: () => void;
  /** Replaces one document in the list (after an edit or a re-index). */
  replace: (doc: CompanyDocument) => void;
  /** Reloads the group and the documents (after a 404 or a lost permission). */
  reloadAll: () => void;
}

function DocTypeOptions() {
  return (
    <>
      <option value="">Belum ditentukan</option>
      {DOC_TYPES.map((t) => (
        <option key={t} value={t}>
          {DOC_TYPE_LABEL[t]}
        </option>
      ))}
    </>
  );
}

function UploadForm({ group, onUploaded, reloadAll }: { group: CompanyGroup | null; onUploaded: (doc: CompanyDocument) => void; reloadAll: () => void }) {
  const picker = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [docType, setDocType] = useState<CompanyDocType | "">("");
  const [kind, setKind] = useState<CompanyDocKind>("persyaratan");
  const [title, setTitle] = useState("");
  const [shared, setShared] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    const next = e.currentTarget.files?.[0] ?? null;
    setDone(null);
    setError(null);
    if (!next) return;
    const problem = uploadProblem(next);
    setFile(problem ? null : next);
    setFileError(problem);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!file) {
      setFileError("Pilih file yang akan diunggah.");
      return;
    }
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const doc = await API.uploadCompanyDocument({ file, kind, docType: docType || null, title: title.trim() || undefined, shared: group ? shared : undefined });
      onUploaded(doc);
      setDone(`${doc.title} diunggah${doc.scope === "grup" ? " dan dibagikan ke grup" : ""}.`);
      setFile(null);
      setTitle("");
      setDocType("");
      if (picker.current) picker.current.value = "";
    } catch (err) {
      setError(companyErrorMessage(err));
      if (needsReload(err)) reloadAll();
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="form-stack" noValidate onSubmit={submit} aria-label="Unggah dokumen perusahaan">
      <FilePickField
        id="cd"
        required
        accept={UPLOAD_ACCEPT}
        file={file}
        onPick={onPick}
        inputRef={picker}
        disabled={busy}
        error={fileError}
        hint="Word (.docx), PDF, Excel (.xlsx), atau teks (.txt), maksimal 10 MB."
      />
      <div className="field-row">
        <Field id="cd-type" label="Jenis dokumen">
          <select {...fieldAria("cd-type")} value={docType} onChange={(e) => setDocType(e.currentTarget.value as CompanyDocType | "")}>
            <DocTypeOptions />
          </select>
        </Field>
        <Field id="cd-kind" label="Kategori" hint="Persyaratan: NIB, akta, SBU, pengalaman, CV tenaga ahli.">
          <select {...fieldAria("cd-kind")} value={kind} onChange={(e) => setKind(e.currentTarget.value as CompanyDocKind)}>
            <option value="persyaratan">Persyaratan</option>
            <option value="umum">Umum</option>
          </select>
        </Field>
      </div>
      <Field id="cd-title" label="Judul" hint="Opsional. Tanpa judul, nama file yang dipakai.">
        <input {...fieldAria("cd-title")} value={title} maxLength={300} onChange={(e) => setTitle(e.currentTarget.value)} placeholder="Contoh: NIB CV Mitra Karya 2026" />
      </Field>
      {group ? (
        <div className="field">
          <Switch id="cd-shared" label="Bagikan ke grup" checked={shared} onChange={setShared} describedBy="cd-shared-hint" />
          <p className="field-hint" id="cd-shared-hint">
            {shared ? `Semua anggota ${group.name} dapat membaca dokumen ini.` : "Hanya Anda yang dapat membaca dokumen ini."}
          </p>
        </div>
      ) : null}
      {error ? <Notice tone="danger" title="Dokumen belum diunggah">{error}</Notice> : null}
      {done ? <Notice tone="success" title="Dokumen diunggah">{done}</Notice> : null}
      <div className="button-row">
        <button type="submit" className="btn" aria-busy={busy || undefined} disabled={busy}>
          <Glyph name="add" />
          <span>{busy ? "Mengunggah" : "Unggah dokumen"}</span>
        </button>
      </div>
    </form>
  );
}

function metaOf(d: CompanyDocument): string {
  return [d.uploadedBy ? `Diunggah ${d.uploadedBy.name}` : "Pengunggah sudah tidak ada", formatDate(d.createdAt), formatFileSize(d.sizeBytes)].filter(Boolean).join(" · ");
}

function EditDialog({ doc, onClose, onSaved, reloadAll }: { doc: CompanyDocument | null; onClose: () => void; onSaved: (d: CompanyDocument) => void; reloadAll: () => void }) {
  const [docType, setDocType] = useState<CompanyDocType | "">("");
  const [kind, setKind] = useState<CompanyDocKind>("persyaratan");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!doc) return;
    setDocType(doc.docType ?? "");
    setKind(doc.kind);
    setTitle(doc.title);
    setError(null);
  }, [doc]);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!doc || busy) return;
    const patch: API.CompanyDocumentPatch = {};
    if ((docType || null) !== doc.docType) patch.docType = docType || null;
    if (kind !== doc.kind) patch.kind = kind;
    const t = title.trim();
    if (!t) {
      setError("Judul wajib diisi.");
      return;
    }
    if (t !== doc.title) patch.title = t;
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      onSaved(await API.updateCompanyDocument(doc.id, patch));
      onClose();
    } catch (err) {
      setError(companyErrorMessage(err));
      if (needsReload(err)) reloadAll();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={doc !== null}
      title="Ubah dokumen"
      description={doc ? doc.fileName : undefined}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={onClose}>
            Batal
          </button>
          <button type="submit" form="cd-edit-form" className="btn" aria-busy={busy || undefined} disabled={busy}>
            Simpan
          </button>
        </>
      }
    >
      <form id="cd-edit-form" className="form-stack" noValidate onSubmit={save}>
        <Field id="cd-edit-title" label="Judul" required>
          <input {...fieldAria("cd-edit-title")} data-autofocus value={title} maxLength={300} onChange={(e) => setTitle(e.currentTarget.value)} />
        </Field>
        <Field id="cd-edit-type" label="Jenis dokumen">
          <select {...fieldAria("cd-edit-type")} value={docType} onChange={(e) => setDocType(e.currentTarget.value as CompanyDocType | "")}>
            <DocTypeOptions />
          </select>
        </Field>
        <Field id="cd-edit-kind" label="Kategori">
          <select {...fieldAria("cd-edit-kind")} value={kind} onChange={(e) => setKind(e.currentTarget.value as CompanyDocKind)}>
            <option value="persyaratan">Persyaratan</option>
            <option value="umum">Umum</option>
          </select>
        </Field>
        {error ? <Notice tone="danger" title="Perubahan belum disimpan">{error}</Notice> : null}
      </form>
    </Dialog>
  );
}

export function CompanyDocuments({ group, documents, loading, error, reload, replace, reloadAll }: CompanyDocumentsProps) {
  const [busy, setBusy] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ id: string; message: string } | null>(null);
  const [editing, setEditing] = useState<CompanyDocument | null>(null);
  const [deleting, setDeleting] = useState<CompanyDocument | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const list = documents ?? [];
  const anyPending = list.some((d) => d.indexStatus === "pending");

  // A document still Diproses is read again until it is ready (or failed).
  useEffect(() => {
    if (!anyPending) return;
    const t = setTimeout(reload, PENDING_POLL_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyPending, documents]);

  const fail = (id: string, err: unknown) => {
    setRowError({ id, message: companyErrorMessage(err) });
    if (needsReload(err)) reloadAll();
  };

  const download = async (d: CompanyDocument) => {
    if (busy) return;
    setBusy(d.id);
    setRowError(null);
    try {
      saveFile(await API.downloadCompanyDocument(d.id));
    } catch (err) {
      fail(d.id, err);
    } finally {
      setBusy(null);
    }
  };

  const reindex = async (d: CompanyDocument) => {
    if (busy) return;
    setBusy(d.id);
    setRowError(null);
    try {
      replace(await API.reindexCompanyDocument(d.id));
    } catch (err) {
      fail(d.id, err);
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!deleting || deleteBusy) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await API.deleteCompanyDocument(deleting.id);
      setDeleting(null);
      reload();
    } catch (err) {
      setDeleteError(companyErrorMessage(err));
      if (needsReload(err)) reloadAll();
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <section className="card" aria-labelledby="cd-heading">
      <div className="section-head">
        <div className="section-head-text">
          <h2 id="cd-heading" className="card-title">
            Dokumen perusahaan
          </h2>
          <p className="section-lead">
            {group ? `Dokumen yang dibagikan dapat dibaca semua anggota ${group.name}. Dokumen pribadi hanya untuk Anda.` : "Tanpa grup, dokumen yang Anda unggah hanya untuk Anda."}
          </p>
        </div>
      </div>

      <UploadForm group={group} onUploaded={() => reload()} reloadAll={reloadAll} />

      {loading && !documents ? (
        <SkeletonRows count={3} label="Memuat dokumen perusahaan" />
      ) : error && !documents ? (
        <Notice
          tone="danger"
          title="Dokumen perusahaan belum dapat dimuat"
          action={
            <button type="button" className="btn btn-secondary" onClick={reload}>
              Muat ulang
            </button>
          }
        >
          <p>{companyErrorMessage(error)}</p>
        </Notice>
      ) : list.length === 0 ? (
        <EmptyState icon="documentText" title="Belum ada dokumen perusahaan" body="Unggah NIB, akta, SBU, pengalaman, atau CV tenaga ahli agar Pluto dapat memakainya saat menyusun dokumen." />
      ) : (
        <>
          <p className="form-note">{formatCount(list.length)} dokumen</p>
          <ul className="rows" aria-label="Dokumen perusahaan">
            {list.map((d) => (
              <li key={d.id} className="row" data-status={d.indexStatus}>
                <span className="row-main">
                  <span className="row-title">{d.title}</span>
                  <span className="row-meta">
                    <Badge>{d.docTypeLabel ?? (d.docType ? DOC_TYPE_LABEL[d.docType] : "Jenis belum ditentukan")}</Badge> <Badge>{KIND_LABEL[d.kind]}</Badge>{" "}
                    <Badge tone={d.scope === "grup" ? "info" : "neutral"}>{d.scope === "grup" ? "Grup" : "Pribadi"}</Badge> <Badge tone={INDEX_TONE[d.indexStatus]}>{INDEX_LABEL[d.indexStatus]}</Badge>
                  </span>
                  <span className="row-meta">{metaOf(d)}</span>
                  {d.indexStatus === "failed" ? (
                    <span className="row-meta">{d.indexError ? `${d.indexError}. ` : ""}Dokumen tersimpan, tetapi Pluto belum dapat memakainya. Coba Proses ulang.</span>
                  ) : null}
                  {rowError?.id === d.id ? (
                    <span className="pk-item-error" role="alert">
                      <Glyph name="danger" size={16} />
                      <span>{rowError.message}</span>
                    </span>
                  ) : null}
                </span>
                <span className="row-side">
                  {d.indexStatus === "failed" && d.canManage ? (
                    <button type="button" className="btn btn-secondary" disabled={busy !== null} aria-busy={busy === d.id || undefined} aria-label={`Proses ulang ${d.title}`} onClick={() => void reindex(d)}>
                      <Glyph name="refresh" />
                      <span>Proses ulang</span>
                    </button>
                  ) : null}
                  <button type="button" className="btn btn-secondary" disabled={busy !== null} aria-busy={busy === d.id || undefined} aria-label={`Unduh ${d.title}`} onClick={() => void download(d)}>
                    <Glyph name="documentDownload" />
                    <span>Unduh</span>
                  </button>
                  {d.canManage ? (
                    <Menu
                      label={`Aksi untuk ${d.title}`}
                      items={[
                        { id: "edit", label: "Ubah", icon: "edit2", onSelect: () => setEditing(d) },
                        {
                          id: "delete",
                          label: "Hapus",
                          icon: "trash",
                          tone: "danger",
                          onSelect: () => {
                            setDeleteError(null);
                            setDeleting(d);
                          },
                        },
                      ]}
                    />
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      <EditDialog doc={editing} onClose={() => setEditing(null)} onSaved={replace} reloadAll={reloadAll} />

      <Dialog
        open={deleting !== null}
        title="Hapus dokumen?"
        description={deleting ? `${deleting.title} dihapus dari Data Perusahaan${deleting.scope === "grup" ? " untuk semua anggota grup" : ""} dan tidak lagi dipakai Pluto. Tindakan ini tidak dapat dibatalkan.` : undefined}
        onClose={() => setDeleting(null)}
        busy={deleteBusy}
        size="sm"
        footer={
          <>
            <button type="button" className="btn btn-secondary" disabled={deleteBusy} onClick={() => setDeleting(null)}>
              Batal
            </button>
            <button type="button" className="btn" data-tone="danger" aria-busy={deleteBusy || undefined} disabled={deleteBusy} onClick={() => void remove()}>
              Hapus dokumen
            </button>
          </>
        }
      >
        {deleteError ? <Notice tone="danger" title="Dokumen belum dihapus">{deleteError}</Notice> : <p className="form-note">Pastikan dokumen ini memang tidak dipakai lagi.</p>}
      </Dialog>
    </section>
  );
}
